import React from 'react';
import { BsDownload, BsPrinter } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, HStack, Icon, IconButton, Text, Tooltip } from '@chakra-ui/react';

import { CypressIds } from '../../../constants/cypressIds';
import { CertSubmissionDuskIds } from '../../../constants/duskIds';
import { inAppText } from '../../../constants/inAppText';
import CertificateCreationBlueprintType from '../../../types/CertificateCreationBlueprintType';

import CertHolderInfoBlock from './CertHolderInfoBlock';
import InsuredInfoBlock from './InsuredInfoBlock';
import PreviousSubmissionsBlock from './PreviousSubmissionsBlock';
import RequiredCoverageInfoBlock from './RequiredCoverageInfoBlock';
import RequiredDocumentsBlock from './RequiredDocumentsBlock';
import RequiredLanguageBlock from './RequiredLanguageBlock';

const RequirementStep = ({ blueprint }) => {
  const dispatch = useDispatch();
  const company = useSelector(blueprint.selectCompany);
  const job = useSelector(blueprint.selectJob);
  const department = useSelector(blueprint.selectDepartment);
  const insured = useSelector(blueprint.selectInsured);
  const options = useSelector(blueprint.selectOptions);
  const documentRequirements = useSelector(blueprint.selectDocumentRequirements);
  const minimumRequirements = useSelector(blueprint.selectMinimumRequirements);
  const languageRequirements = useSelector(blueprint.selectLanguageRequirements);
  const insuranceRequirement = useSelector(blueprint.selectInsuranceRequirement);
  const { chooseAnyPolicyType } = useSelector(blueprint.selectOptions);

  return (
    <Box overflowY="auto" px={6} width="100%">
      <Flex flexFlow="row nowrap" justifyContent="space-between" mt="2.75rem">
        <Text textStyle="displayXs">Insurance Requirements</Text>
        <HStack spacing={4}>
          <Tooltip label={inAppText.tooltips.comingSoon}>
            <Box>
              <IconButton
                aria-label="Download Requirements"
                icon={<Icon as={BsDownload} boxSize={6} />}
                variant="accent"
              />
            </Box>
          </Tooltip>
          <Tooltip label={inAppText.tooltips.comingSoon}>
            <Box>
              <IconButton
                aria-label="Print Requirements"
                icon={<Icon as={BsPrinter} boxSize={6} />}
                variant="accent"
              />
            </Box>
          </Tooltip>
          <Button
            data-cy={CypressIds.certStartSubmissionButton}
            dusk={CertSubmissionDuskIds.startSubmissionButton}
            variant="gradientSuccess"
            onClick={() => dispatch(blueprint.nextStep(chooseAnyPolicyType ? 1 : 2))}
          >
            Start Submission
          </Button>
        </HStack>
      </Flex>

      {options.showPreviousSubmissions && (
        <PreviousSubmissionsBlock blueprint={blueprint} />
      )}

      <Flex flexBasis="100%" flexFlow="row wrap" justifyContent="space-between" mt={8}>
        <Flex flexBasis="48%" flexFlow="column">
          {options.showCertHolderInformation && (<CertHolderInfoBlock company={company} department={department} job={job} />)}
          <RequiredDocumentsBlock documentRequirements={documentRequirements} />
          <RequiredLanguageBlock languageRequirements={languageRequirements} />
        </Flex>
        <Flex flexBasis="48%" flexFlow="column">
          {options.showInsuredInformation && (<InsuredInfoBlock insured={insured} requirement={insuranceRequirement} />)}
          <RequiredCoverageInfoBlock minimumRequirements={minimumRequirements} />
        </Flex>
      </Flex>
    </Box>
  );
};

RequirementStep.propTypes = { blueprint: CertificateCreationBlueprintType };

export default RequirementStep;
